import {
    type AncestorNode,
    type Block,
    BlockRange,
    type Document,
    type Value,
} from '@gitbook/slate';

import {
    BLOCK_CODE,
    BLOCK_COLUMN,
    BLOCK_COLUMNS,
    BLOCK_CONTENT_REF,
    BLOCK_DIVIDER,
    BLOCK_DRAWING,
    BLOCK_EMBED,
    BLOCK_EXPANDABLE,
    BLOCK_FILE,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_HINT,
    BLOCK_IF,
    BLOCK_IMAGE,
    BLOCK_IMAGES,
    BLOCK_INTEGRATION,
    BLOCK_LIST_ITEM,
    BLOCK_LIST_OL,
    BLOCK_LIST_TASKS,
    BLOCK_LIST_UL,
    BLOCK_MATH,
    BLOCK_OPENAPI,
    BLOCK_OPENAPI_OPERATION,
    BLOCK_OPENAPI_SCHEMAS,
    BLOCK_OPENAPI_WEBHOOK,
    BLOCK_PARAGRAPH,
    BLOCK_PROMPT,
    BLOCK_QUOTE,
    BLOCK_REUSABLE_CONTENT,
    BLOCK_STEPPER,
    BLOCK_STEPPER_STEP,
    BLOCK_TABLE,
    BLOCK_TABS,
    BLOCK_TABS_ITEM,
    BLOCK_UPDATE,
    BLOCK_UPDATES,
    FRAGMENT_ANNOTATION_BODY,
    FRAGMENT_EXPANDABLE_BODY,
    FRAGMENT_EXPANDABLE_TITLE,
    FRAGMENT_TABLE_CELL,
    LIST_BLOCKS,
    VOID_BLOCKS,
} from '../types';

/**
 * Blocks that can be inserted almost everywhere
 */
const ESSENTIALS_BLOCKS = [
    BLOCK_PARAGRAPH,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_DIVIDER,
    ...LIST_BLOCKS,
    BLOCK_LIST_ITEM, // List item are normalized as list
];

/**
 * Blocks that can be inserted at the top level.
 */
const TOP_LEVEL_BLOCKS = [
    ...ESSENTIALS_BLOCKS,
    BLOCK_CODE,
    BLOCK_COLUMNS,
    BLOCK_CONTENT_REF,
    BLOCK_DRAWING,
    BLOCK_EMBED,
    BLOCK_EXPANDABLE,
    BLOCK_FILE,
    BLOCK_HINT,
    BLOCK_PROMPT,
    BLOCK_IF,
    BLOCK_IMAGES,
    BLOCK_INTEGRATION,
    BLOCK_MATH,
    BLOCK_OPENAPI,
    BLOCK_OPENAPI_OPERATION,
    BLOCK_OPENAPI_SCHEMAS,
    BLOCK_OPENAPI_WEBHOOK,
    BLOCK_QUOTE,
    BLOCK_REUSABLE_CONTENT,
    BLOCK_STEPPER,
    BLOCK_TABLE,
    BLOCK_TABS,
    BLOCK_UPDATES,
    // Will be processed by normalization and tranformed into other blocks:
    BLOCK_IMAGE,
];

/**
 * Blocks that can be inserted in a tab item.
 */
const TABS_ITEM_BLOCKS = [
    ...ESSENTIALS_BLOCKS,
    BLOCK_CONTENT_REF,
    BLOCK_TABLE,
    BLOCK_CODE,
    BLOCK_EMBED,
    BLOCK_EXPANDABLE,
    BLOCK_FILE,
    BLOCK_IMAGES,
    BLOCK_DRAWING,
    BLOCK_HINT,
    BLOCK_PROMPT,
    BLOCK_QUOTE,
    BLOCK_IF,
    BLOCK_MATH,
    BLOCK_INTEGRATION,
    BLOCK_REUSABLE_CONTENT,
    BLOCK_STEPPER,
];

/**
 * Blocks that can be inserted in a stepper step.
 */
const STEPPER_STEP_BLOCKS = [...TABS_ITEM_BLOCKS, BLOCK_TABS];

/**
 * Blocks that can be inserted in an update block.
 */
const UPDATE_BLOCK_BLOCKS = [...TABS_ITEM_BLOCKS, BLOCK_TABS];

/**
 * Blocks that can be inserted in a list.
 */
const LIST_ITEM_BLOCKS = [...ESSENTIALS_BLOCKS, BLOCK_IMAGES];

/**
 * Blocks that can be inserted in an if block.
 */
export const IF_BLOCK_CONTENT_BLOCKS = TOP_LEVEL_BLOCKS;

export const COLUMN_CONTENT_BLOCKS = [
    ...ESSENTIALS_BLOCKS,
    BLOCK_CODE,
    BLOCK_CONTENT_REF,
    BLOCK_DRAWING,
    BLOCK_EMBED,
    BLOCK_EXPANDABLE,
    BLOCK_FILE,
    BLOCK_HINT,
    BLOCK_PROMPT,
    BLOCK_IF,
    BLOCK_IMAGES,
    BLOCK_INTEGRATION,
    BLOCK_MATH,
    BLOCK_QUOTE,
    BLOCK_TABLE,
    BLOCK_STEPPER,
];

const INSERTION_MAPS = {
    [BLOCK_QUOTE]: ESSENTIALS_BLOCKS.concat([BLOCK_QUOTE]),
    [BLOCK_LIST_ITEM]: LIST_ITEM_BLOCKS,
    [BLOCK_HINT]: [...ESSENTIALS_BLOCKS, BLOCK_EMBED, BLOCK_INTEGRATION, BLOCK_TABLE],
    [BLOCK_PROMPT]: [BLOCK_CODE],
    [BLOCK_IF]: IF_BLOCK_CONTENT_BLOCKS,
    [BLOCK_TABS_ITEM]: TABS_ITEM_BLOCKS,
    [BLOCK_LIST_UL]: LIST_ITEM_BLOCKS,
    [BLOCK_LIST_OL]: LIST_ITEM_BLOCKS,
    [BLOCK_LIST_TASKS]: LIST_ITEM_BLOCKS,
    [BLOCK_STEPPER]: [BLOCK_STEPPER_STEP],
    [BLOCK_STEPPER_STEP]: STEPPER_STEP_BLOCKS,
    [BLOCK_UPDATES]: [BLOCK_UPDATE],
    [BLOCK_UPDATE]: UPDATE_BLOCK_BLOCKS,
    [BLOCK_COLUMN]: COLUMN_CONTENT_BLOCKS,
};

/**
 * List of blocks allowed for a fragment type.
 * If not specified, all TOP_LEVEL_BLOCKS are allowed in the fragment.
 */
export const FRAGMENT_ALLOWED_BLOCKS = {
    [FRAGMENT_EXPANDABLE_TITLE]: [
        BLOCK_PARAGRAPH,
        BLOCK_HEADING_1,
        BLOCK_HEADING_2,
        BLOCK_HEADING_3,
    ],
    [FRAGMENT_EXPANDABLE_BODY]: [
        ...ESSENTIALS_BLOCKS,
        BLOCK_EMBED,
        BLOCK_FILE,
        BLOCK_IMAGES,
        BLOCK_CODE,
        BLOCK_TABLE,
        BLOCK_HINT,
        BLOCK_PROMPT,
        BLOCK_QUOTE,
        BLOCK_IF,
        BLOCK_REUSABLE_CONTENT,
        BLOCK_INTEGRATION,
        BLOCK_STEPPER,
        BLOCK_TABS,
    ],
    [FRAGMENT_ANNOTATION_BODY]: [...ESSENTIALS_BLOCKS, BLOCK_CODE],
    [FRAGMENT_TABLE_CELL]: [
        ...ESSENTIALS_BLOCKS,
        BLOCK_CODE,
        BLOCK_EMBED,
        BLOCK_FILE,
        BLOCK_HINT,
        BLOCK_IF,
        BLOCK_MATH,
        BLOCK_PROMPT,
        BLOCK_QUOTE,
        BLOCK_STEPPER,
        BLOCK_TABS,
    ],
};

/**
 * Blocks that can never appear anywhere inside a fragment, even nested in a block that would
 * otherwise accept them. Mirrors those blocks' `fragmentParent` schema rule, which normalization
 * enforces by hoisting the block back out of the fragment, or by deleting it.
 */
const FRAGMENT_FORBIDDEN_BLOCKS: Record<string, string[]> = {
    [FRAGMENT_EXPANDABLE_BODY]: [BLOCK_EXPANDABLE],
    [FRAGMENT_TABLE_CELL]: [BLOCK_TABLE, BLOCK_EXPANDABLE],
};

/**
 * Blocks that only wrap content, and so never govern what can be inserted in them.
 */
const TRANSPARENT_BLOCKS = [BLOCK_IF];

/**
 * Check if a block can be inserted in a parent.
 */
export function canInsertBlockIn(parent: AncestorNode, type: string): boolean {
    if (parent.object === 'document') {
        return TOP_LEVEL_BLOCKS.includes(type);
    }

    if (parent.object === 'fragment') {
        const allowedBlocksInFragment =
            (parent.type
                ? FRAGMENT_ALLOWED_BLOCKS[parent.type as keyof typeof FRAGMENT_ALLOWED_BLOCKS]
                : null) || TOP_LEVEL_BLOCKS;
        return allowedBlocksInFragment.includes(type);
    }

    if (VOID_BLOCKS.includes(parent.type)) {
        return false;
    }

    const insertionMap = INSERTION_MAPS[parent.type as keyof typeof INSERTION_MAPS];
    return insertionMap ? insertionMap.includes(type) : false;
}

/**
 * Check if a block can be inserted in a parent, also honouring the fragment that parent lives in.
 *
 * Prefer this over `canInsertBlockIn` wherever the document is at hand: a hint accepts a table,
 * but a hint inside a table cell does not, because a table can never be nested in another
 * table's cell. `canInsertBlockIn` only ever sees the direct parent, so it cannot tell the two
 * apart and would offer an insertion that normalization immediately undoes.
 */
export function canInsertBlockInside(
    document: Document,
    parent: AncestorNode,
    type: string
): boolean {
    if (!canInsertBlockIn(parent, type)) {
        return false;
    }

    return !getForbiddenBlocksInside(document, parent)?.includes(type);
}

/**
 * Check if moving an existing block into a parent would put the block, or one nested in it, in a
 * fragment that never accepts it.
 *
 * `canInsertBlockInside` only sees the moved block's type: an expandable body accepts tabs, but
 * not tabs holding an expandable, which normalization would delete once moved there.
 */
export function isBlockForbiddenInside(
    document: Document,
    parent: AncestorNode,
    block: Block
): boolean {
    const forbiddenBlocks = getForbiddenBlocksInside(document, parent);
    if (!forbiddenBlocks) {
        return false;
    }

    return (
        forbiddenBlocks.includes(block.type) ||
        block.findDescendant(
            (node) => node.object === 'block' && forbiddenBlocks.includes(node.type)
        ) !== null
    );
}

/**
 * Check if a block can be inserted at the current selection.
 */
export function canInsertBlockAt(
    value: Value,
    type: string,
    options: {
        /**
         * If true, the insertion is allowed to split the current block.
         * @default true
         */
        allowSplitBlock?: boolean;
    } = {}
): boolean {
    const { allowSplitBlock = true } = options;
    const { selection, startBlock } = value;

    if (startBlock) {
        if (!allowSplitBlock && !startBlock.isEmpty) {
            return false;
        }

        const parent = getInsertionParent(value.document, startBlock.key);
        if (parent) {
            return canInsertBlockIn(parent, type);
        }

        return false;
    }

    if (BlockRange.isBlockRange(selection) && selection.anchorBlock) {
        const parent = getInsertionParent(value.document, selection.anchorBlock);
        if (parent) {
            return canInsertBlockIn(parent, type);
        }
    }

    return false;
}

/**
 * Container that governs what can be inserted around a node: its parent, looking through
 * blocks that only wrap content (an `if` block can never host more than its own container).
 *
 * Use this instead of `document.getParent` when resolving the parent to pass to
 * `canInsertBlockIn`.
 */
export function getInsertionParent(document: Document, nodeKey: string): AncestorNode | null {
    let parent = document.getParent(nodeKey);

    while (parent && parent.object === 'block' && TRANSPARENT_BLOCKS.includes(parent.type)) {
        parent = document.getParent(parent.key);
    }

    return parent;
}

/**
 * Blocks that can never appear in the fragment a parent lives in, if any.
 */
function getForbiddenBlocksInside(document: Document, parent: AncestorNode): string[] | undefined {
    const fragment = parent.object === 'fragment' ? parent : document.getFragmentParent(parent.key);

    if (fragment?.object !== 'fragment' || !fragment.type) {
        return undefined;
    }

    return FRAGMENT_FORBIDDEN_BLOCKS[fragment.type];
}
